import { useQuery } from '@tanstack/react-query';

import { Roles } from 'api/tokenStorage';
import { api } from 'api/v1';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';

export interface UserRole {
  id: number;
  title: string;
  description?: string | null;
}

interface UserRolesResponse {
  roles: UserRole[];
}

export const useUserRoles = (enabled = true) => {
  const { data: session } = useAuthSession(enabled);
  const userId = isAuthSessionActive(session) ? session?.id : undefined;

  return useQuery({
    queryKey: ['user', 'roles', userId ?? 'anon'],
    queryFn: async () => {
      const response = await api.get<UserRolesResponse>('/user/roles');
      return response.data.roles;
    },
    enabled: enabled && Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
};

const isTutorTitle = (title: string): boolean => {
  const lower = title.toLowerCase();
  return lower.includes('tutor') || lower.includes('репетитор');
};

const isStudentTitle = (title: string): boolean => {
  const lower = title.toLowerCase();
  return (
    lower.includes('student') ||
    lower.includes('ученик') ||
    lower.includes('студент')
  );
};

/** True only when API roles include both tutor and student. No cookie fallback. */
export const hasDualTutorStudentRoles = (roles: UserRole[] | undefined): boolean => {
  if (!roles?.length) {
    return false;
  }

  const hasTutor = roles.some(
    (role) => role.id === Roles.tutor || isTutorTitle(role.title ?? ''),
  );
  const hasStudent = roles.some(
    (role) => role.id === Roles.student || isStudentTitle(role.title ?? ''),
  );
  return hasTutor && hasStudent;
};
